// CANONICAL COPY (GH #7115). src/utils/runbookReview.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/utils/runbookReview.js
 *
 * Review currency for a BC/DR runbook (bcdrRunbooks).
 *
 * A runbook whose status is 'active' asserts that a system can be recovered by
 * following it. minimum-requirements/bcdr.json's mustNeverClaim forbids reading
 * "a procedure exists" as "the service is recoverable", so that assertion has
 * to expire: this module is the single answer to "when is this runbook's review
 * due, and does the last review still cover what the runbook now says".
 *
 * Two rules decide everything here, and both exist because the optimistic
 * answer is the dangerous one.
 *
 * 1. THE DUE DATE IS THE EARLIEST THE PRODUCT HOLDS, never a preferred source.
 *    A runbook can carry its own `reviewDate`, and it can point at a document
 *    (BCDR-B-001 makes `documentId` the single source of truth for that link)
 *    which carries its own `nextReview`. Picking one by provenance — "the
 *    document wins when there is one" — lets a 2027 document date discard a
 *    2026 record date the continuity manager typed, and the runbook reads as
 *    current on the strength of the laxer of two dates the product already
 *    holds. Earliest-wins makes that impossible, and a missing date on one
 *    source never erases the other.
 *
 * 1b. AN EXERCISE THAT DID NOT PASS BRINGS THE REVIEW FORWARD, AND NOTHING
 *    EVER PUSHES IT BACK. A DR test that named this runbook and came back
 *    'fail', 'partial' or unassessed is real evidence that the procedure needs
 *    reading again, so it is a fourth source of the due date — one that can
 *    only make the review sooner.
 *
 *    A PASSED exercise deliberately does NOT refresh review currency, however
 *    strong it looks. An exercise asserts "on date D, following this recovered
 *    the system"; a review asserts "this still describes the system as it is
 *    now". obl-bc-exercise carries two criteria precisely because those are
 *    different propositions, and letting a pass renew the review would let one
 *    self-graded tabletop satisfy both — permanently, since the exercise window
 *    and the review cadence are both a year. A link between two governance
 *    records may pull a compliance-bearing date earlier; it may never push one
 *    later.
 *
 * 2. A REVIEW IS A CLAIM ABOUT A VERSION. `reviewIsCurrent` mirrors
 *    src/components/gap/reportLifecycle.js, which learned this first: a review
 *    that survives an edit has the product asserting that a named person
 *    reviewed text they never saw. Each review pins the `updatedAt` it covered;
 *    editing the runbook moves `updatedAt` past the pin and the review goes
 *    STALE — not erased, and still visible as history, but no longer evidence.
 *
 * The review log is append-only. A review can be withdrawn (it keeps its row,
 * gains `withdrawnAt`/`withdrawnBy` and stops counting) because a governance act
 * the organisation cannot correct is not one — again reportLifecycle.js's rule.
 * Nothing here overwrites a previous review: "when was it last reviewed" is a
 * derived value, and "how often, by whom, and of what" survives.
 */

/**
 * The review cadence, deliberately equal to obl-bc-exercise's own
 * `cadence.frequency` ('1y'). One cadence for continuity, not a second one
 * invented here — if that obligation's cadence changes, this follows it.
 */
const REVIEW_CADENCE_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * What a review concluded. 'changes-needed' is the one that owes work: the
 * caller routes it into the existing task system rather than leaving a
 * weakness recorded and unowned (bcdr.json's doneLooksLike — "identified
 * weaknesses become tracked improvements").
 */
const REVIEW_OUTCOMES = [
  { value: 'no-change', label: 'Reviewed — no change needed' },
  { value: 'updated', label: 'Reviewed — updated during review' },
  { value: 'changes-needed', label: 'Reviewed — changes needed' },
];

const ms = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Every review, newest first. Withdrawn reviews are included — they are history. */
function reviewHistory(runbook) {
  return [...((runbook && runbook.reviews) || [])].sort(
    (a, b) => (ms(b?.at) || 0) - (ms(a?.at) || 0)
  );
}

/** The most recent review that has not been withdrawn, or null. */
function latestReview(runbook) {
  return reviewHistory(runbook).find((r) => r && !r.withdrawnAt) || null;
}

/**
 * Does the latest review still cover what the runbook currently says?
 *
 * A review with no version pin is treated as stale rather than current: under
 * TRYGGR_INTENT.md §5, under-claiming a review is safe and over-claiming one is
 * not, and a pin is how a review earns the claim. Same reasoning for a runbook
 * that carries neither `updatedAt` nor `createdAt` — nothing to compare, so
 * nothing is asserted.
 */
function reviewIsCurrent(runbook) {
  const review = latestReview(runbook);
  if (!review) return false;
  const pinned = ms(review.coversUpdatedAt);
  if (pinned === null) return false;
  const changedAt = ms(runbook?.updatedAt) ?? ms(runbook?.createdAt);
  if (changedAt === null) return false;
  return pinned >= changedAt;
}

/**
 * The DR tests / exercises that named this runbook, newest conducted first.
 *
 * The edge is stored once, on the DR test (`linkedRunbookIds`), and the
 * runbook's view of it is derived — so the two records can never disagree
 * about whether an exercise covered a runbook.
 */
function exercisesForRunbook(runbook, drTests = []) {
  if (!runbook?.id) return [];
  return (drTests || [])
    .filter((t) => t && (t.linkedRunbookIds || []).includes(runbook.id))
    .sort((a, b) => (ms(b?.conductedDate) || 0) - (ms(a?.conductedDate) || 0));
}

/**
 * The most recent exercise that actually PASSED, or null.
 *
 * This is the only exercise a surface may present as "last exercised": a
 * failed one demonstrates that capability is absent and an unassessed one
 * demonstrates nothing, so neither may stand under that label. They remain
 * visible as history — see exercisesForRunbook.
 */
function lastPassedExercise(runbook, drTests = []) {
  return (
    exercisesForRunbook(runbook, drTests).find(
      (t) => t.status === 'completed' && t.conductedDate && t.outcome === 'pass'
    ) || null
  );
}

/**
 * When this runbook's review is due, as the earliest date any source gives.
 *
 * Sources, all optional, none preferred over another:
 *   - `runbook.reviewDate`         — typed on the runbook itself
 *   - linked document `nextReview` — via runbook.documentId (BCDR-B-001)
 *   - derived                      — (last review ?? creation) + the cadence
 *   - unpassed exercise            — the date a linked DR test failed, was
 *                                    partial, or was never assessed
 *
 * The derived source is what makes this Enter Once rather than a third date to
 * type: the product already knows when the runbook was written and when it was
 * last reviewed. A runbook with no anchor at all — no dates of any kind —
 * returns null, which callers read as "not current", never as "not due".
 *
 * `documents` is the tenant's document list; pass [] when it is unavailable.
 * Note that both `bcdrRunbooks` and `documents` are therefore inputs, which is
 * why the auto-evidence checker declares both as state dependencies: a plan
 * that withholds documents must not silently change which date governs.
 */
function resolveReviewDueAt(runbook, documents = [], drTests = []) {
  if (!runbook) return { dueAt: null, sources: [] };

  const sources = [];

  const own = ms(runbook.reviewDate);
  if (own !== null) sources.push({ source: 'record', at: runbook.reviewDate, ms: own });

  if (runbook.documentId) {
    const doc = (documents || []).find((d) => d && d.id === runbook.documentId);
    const docDue = ms(doc?.nextReview);
    if (docDue !== null) sources.push({ source: 'document', at: doc.nextReview, ms: docDue });
  }

  const review = latestReview(runbook);
  const anchor = ms(review?.at) ?? ms(runbook.createdAt);
  if (anchor !== null) {
    const derived = new Date(anchor + REVIEW_CADENCE_MS).toISOString();
    sources.push({
      source: review ? 'derived-from-last-review' : 'derived-from-creation',
      at: derived,
      ms: anchor + REVIEW_CADENCE_MS,
    });
  }

  // An exercise that did not pass is evidence this procedure needs reading
  // again, dated the day it was conducted. Only completed exercises count —
  // a planned one has demonstrated nothing either way — and a PASS is
  // deliberately absent from this list: it may not renew the review.
  const failedExercise = exercisesForRunbook(runbook, drTests)
    .filter((t) => t.status === 'completed' && t.conductedDate && t.outcome !== 'pass')
    .map((t) => ({ source: 'unpassed-exercise', at: t.conductedDate, ms: ms(t.conductedDate) }))
    .filter((x) => x.ms !== null)
    .sort((a, b) => a.ms - b.ms)[0];
  if (failedExercise) sources.push(failedExercise);

  if (!sources.length) return { dueAt: null, sources: [] };

  const earliest = sources.reduce((a, b) => (b.ms < a.ms ? b : a));
  return { dueAt: earliest.at, source: earliest.source, sources };
}

/**
 * The whole review state of one runbook, for every surface that asks.
 *
 * `current` is the single answer to "may this runbook be counted as a
 * maintained recovery procedure": it needs a due date that has not passed AND
 * a review that still covers the current version. A runbook that has never
 * been reviewed is not stale — it is simply not yet due, until the cadence
 * catches up with its creation date.
 */
function runbookReviewState(runbook, documents = [], drTests = [], nowMs = Date.now()) {
  const { dueAt, source, sources } = resolveReviewDueAt(runbook, documents, drTests);
  const lastReview = latestReview(runbook);
  const overdue = dueAt !== null && ms(dueAt) < nowMs;
  // Staleness only means something once a review exists to be outlived.
  const stale = !!lastReview && !reviewIsCurrent(runbook);
  return {
    dueAt,
    source: source || null,
    sources,
    lastReview,
    overdue,
    stale,
    unanchored: dueAt === null,
    current: dueAt !== null && !overdue && !stale,
  };
}

/**
 * Build the review entry a "mark as reviewed" action appends.
 *
 * `coversUpdatedAt` is the pin, and the caller passes the `updatedAt` it is
 * about to write with this same save — so the review covers the version it was
 * saved alongside, and the next edit moves past it.
 */
function buildReview({ at, by, byName, outcome, note, coversUpdatedAt }) {
  return {
    id: `rbr-${at}-${by || 'unknown'}`,
    at,
    by: by || null,
    byName: byName || null,
    outcome: outcome || 'no-change',
    note: note || '',
    coversUpdatedAt,
    withdrawnAt: null,
    withdrawnBy: null,
  };
}

module.exports = {
  REVIEW_CADENCE_MS,
  REVIEW_OUTCOMES,
  reviewHistory,
  latestReview,
  reviewIsCurrent,
  exercisesForRunbook,
  lastPassedExercise,
  resolveReviewDueAt,
  runbookReviewState,
  buildReview,
};
